/**
 * DailyAIBriefWidget
 * ==================
 * Inline dashboard widget that shows today's AI Brief — the 5-bullet
 * summary of subtle cost shifts that would otherwise need 30 min of
 * manual analysis. Shortens the loop from "open dashboard → log into
 * 5 tools" to "open dashboard → CARTIE already did the analysis".
 *
 * Loads `/latest` on mount (instant if already generated). If nothing's
 * saved yet, generates one via `/preview`. Never blocks dashboard render
 * if the API is slow — fails quietly and keeps the rest of the dashboard
 * usable.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import {
  Sparkles, RefreshCw, Loader2, BellRing, BellOff, MessageSquare,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeaders = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// Tiny markdown → React renderer for the bullet list.
// Supports the patterns Claude actually produces (lines starting with `•`,
// `- ` or `* `; backticks for inline code). Keeps the parser tiny so we
// don't pull in a 30KB markdown lib for ~5 bullets.
const renderInline = (text) => {
  const parts = String(text || '').split(/(`[^`]+`)/g);
  return parts.map((p, i) => {
    if (p.startsWith('`') && p.endsWith('`')) {
      return (
        <code key={i} className="px-1 py-0.5 rounded bg-emerald-500/10 text-emerald-300 text-[12px] font-mono">
          {p.slice(1, -1)}
        </code>
      );
    }
    return <span key={i}>{p}</span>;
  });
};

const renderBullets = (md) => {
  const lines = String(md || '').split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const bullets = lines
    .map((l) => l.replace(/^([•\-*]\s+)/, ''))
    .filter((l) => l && l !== '_No subtle shifts to flag — quiet day._');
  if (!bullets.length) return null;
  return (
    <ul className="space-y-2.5">
      {bullets.map((b, i) => (
        <li key={i} className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed flex gap-2.5"
            data-testid={`brief-bullet-${i}`}>
          <span className="text-emerald-500 mt-1 shrink-0">▸</span>
          <span className="min-w-0 break-words">{renderInline(b)}</span>
        </li>
      ))}
    </ul>
  );
};

const DailyAIBriefWidget = () => {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [optinLoading, setOptinLoading] = useState(false);
  const [optinEnabled, setOptinEnabled] = useState(false);

  const fetchLatest = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/daily-ai-brief/latest`, { headers: authHeaders() });
      if (data?.available) setDoc(data);
      else await regenerate();  // first-visit path: nothing saved yet
    } catch (e) {
      // Quietly degrade — dashboard must still load if this widget fails.
      console.debug('Daily AI Brief unavailable:', e?.message);
      setDoc(null);
    } finally {
      setLoading(false);
    }
  };

  const regenerate = async () => {
    setRefreshing(true);
    try {
      const { data } = await axios.post(`${API_URL}/daily-ai-brief/preview`, {}, { headers: authHeaders() });
      setDoc(data);
    } catch (e) {
      toast.error("Couldn't refresh today's brief", {
        description: e?.response?.data?.detail || e?.message,
      });
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  };

  const toggleOptin = async (next) => {
    setOptinLoading(true);
    try {
      await axios.post(`${API_URL}/daily-ai-brief/optin`, { enabled: next }, { headers: authHeaders() });
      setOptinEnabled(next);
      toast.success(next
        ? "We'll send tomorrow's brief to your Slack at 09:15 UTC."
        : 'Daily Slack brief paused.');
    } catch (e) {
      toast.error('Could not update setting', {
        description: e?.response?.data?.detail || e?.message,
      });
    } finally { setOptinLoading(false); }
  };

  useEffect(() => { fetchLatest(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, []);

  if (loading) {
    return (
      <Card className="border-emerald-200/60 dark:border-emerald-500/20 bg-white bg-gradient-to-r from-emerald-50/40 via-white to-cyan-50/30 dark:from-emerald-950/20 dark:via-slate-900/60 dark:to-slate-900" data-testid="ai-brief-loading">
        <CardContent className="p-5 flex items-center gap-3 text-sm text-slate-400">
          <Loader2 className="w-4 h-4 animate-spin" /> Reading today's brief…
        </CardContent>
      </Card>
    );
  }

  if (!doc) return null;

  const narrative = doc.narrative_markdown || doc.deterministic_fallback_markdown || '';
  const generatedDate = doc.for_date || new Date().toISOString().slice(0, 10);

  return (
    <Card
      className="border-emerald-200/60 dark:border-emerald-500/20 bg-white bg-gradient-to-r from-emerald-50/40 via-white to-cyan-50/30 dark:from-emerald-950/20 dark:via-slate-900/60 dark:to-slate-900"
      data-testid="daily-ai-brief-widget"
    >
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-500/20 to-cyan-500/15 border border-emerald-500/30 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-emerald-600 dark:text-emerald-300" />
            </div>
            <div>
              <CardTitle className="text-base flex items-center gap-2 text-slate-900 dark:text-white">
                What CARTIE noticed today
                {doc.had_news && (
                  <Badge className="bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30 text-[11px]">
                    {(doc.signals?.customer_share_shifts?.length || 0)
                     + (doc.signals?.ai_feature_margin_flips?.length || 0)
                     + (doc.signals?.money_loser_flips?.length || 0)
                     + (doc.signals?.concentration_creep?.length || 0)
                     + (doc.signals?.commitment_rolloffs?.length || 0)} signal(s)
                  </Badge>
                )}
              </CardTitle>
              <p className="text-xs text-slate-400 dark:text-slate-400 mt-0.5">
                Brief for <span className="font-mono">{generatedDate}</span> · automated FinOps morning analysis
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-xs text-slate-400 dark:text-slate-400" data-testid="ai-brief-optin">
              {optinEnabled
                ? <BellRing className="w-3.5 h-3.5 text-emerald-500" />
                : <BellOff className="w-3.5 h-3.5" />}
              <span className="hidden sm:inline">Daily Slack</span>
              <Switch
                checked={optinEnabled}
                onCheckedChange={toggleOptin}
                disabled={optinLoading}
                data-testid="ai-brief-optin-switch"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={regenerate}
              disabled={refreshing}
              data-testid="ai-brief-refresh-btn"
              className="gap-1.5 border-emerald-200 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-500/30 dark:text-emerald-300 dark:hover:bg-emerald-500/10"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Re-run</span>
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-3">
        {doc.had_news ? (
          renderBullets(narrative) || (
            <p className="text-sm text-slate-400 italic">{narrative}</p>
          )
        ) : (
          <div className="flex items-center gap-2.5 text-sm text-slate-400 dark:text-slate-300" data-testid="ai-brief-quiet-day">
            <MessageSquare className="w-4 h-4 text-emerald-500" />
            <span>
              <strong className="text-slate-800 dark:text-white">Quiet day.</strong>{' '}
              No subtle shifts in customer mix, AI margin, money-losers, concentration or commitments. We'll keep watching.
            </span>
          </div>
        )}
        {doc.had_news && doc.narrative_markdown == null && (
          <p className="text-[11px] text-slate-400 dark:text-slate-400 mt-3 italic">
            Showing the deterministic fallback — the AI narrative was unavailable for this run.
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default DailyAIBriefWidget;
